// Marker zoom tiers and the one zoom floor the map hides everything below (#437).
//
// The floor lived twice: `overlayZoomTier` hid every marker below zoom 12, and the map's `idle`
// handler showed the "return to caza" pill below zoom 11. Both were deliberate and both worked as
// written, but the band `11 <= zoom < 12` inherited the worst of both — no pins to look at and no
// affordance to get back. `MARKER_HIDE_FLOOR_ZOOM` is now the single source of truth: the marker
// floor and the pill read the same number, so the pill is on screen for exactly the range where
// there is nothing to see.

// Below this zoom every marker is culled. Chosen by #341/#344 to keep a zoom-out gesture cheap;
// #437 aligned the return pill to it rather than moving it.
export const MARKER_HIDE_FLOOR_ZOOM = 12;

// Fractional zoom (#341) means a pinch gesture can hover exactly on a marker-tier boundary. A
// plain threshold recomputes a different tier on every tiny fluctuation there and flaps the
// marker DOM back and forth; widening each boundary into a small dead zone, keyed off the
// previous tier, makes the map require a real crossing before it commits to a new tier.
export const ZOOM_TIER_HYSTERESIS = 0.15;

// Per-marker content tier: 0 = dot, 1 = icon, 2 = labeled.
export const markerContentTier = (zoom, prevTier) => {
  if (prevTier == null) return zoom >= 17 ? 2 : zoom >= 14 ? 1 : 0;
  let tier = prevTier;
  if (tier === 0 && zoom >= 14 + ZOOM_TIER_HYSTERESIS) tier = 1;
  if (tier === 1 && zoom >= 17 + ZOOM_TIER_HYSTERESIS) tier = 2;
  if (tier === 2 && zoom < 17 - ZOOM_TIER_HYSTERESIS) tier = 1;
  if (tier === 1 && zoom < 14 - ZOOM_TIER_HYSTERESIS) tier = 0;
  return tier;
};

// Map-level tier: -1 = hidden (below the marker-hide floor), 0/1/2 as above. Drives both the
// zoom_changed animate/skip decision and the MarkerLayer culling floor.
export const overlayZoomTier = (zoom, prevTier) => {
  if (prevTier == null) return zoom < MARKER_HIDE_FLOOR_ZOOM ? -1 : zoom >= 17 ? 2 : zoom >= 14 ? 1 : 0;
  let tier = prevTier;
  if (tier === -1 && zoom >= MARKER_HIDE_FLOOR_ZOOM + ZOOM_TIER_HYSTERESIS) tier = 0;
  if (tier === 0 && zoom >= 14 + ZOOM_TIER_HYSTERESIS) tier = 1;
  if (tier === 1 && zoom >= 17 + ZOOM_TIER_HYSTERESIS) tier = 2;
  if (tier === 2 && zoom < 17 - ZOOM_TIER_HYSTERESIS) tier = 1;
  if (tier === 1 && zoom < 14 - ZOOM_TIER_HYSTERESIS) tier = 0;
  if (tier === 0 && zoom < MARKER_HIDE_FLOOR_ZOOM - ZOOM_TIER_HYSTERESIS) tier = -1;
  return tier;
};

// The return pill's zoom half (#437). Deliberately a pure function of the CURRENT zoom, with no
// hysteresis and no previous-tier memory: the pill is an escape hatch, and a hysteresis band
// would make it depend on the direction the visitor arrived from — the same zoom showing the pill
// on the way out and hiding it on the way back is exactly the flicker to avoid. The markers'
// hysteresis can only ever hold pins on screen slightly INSIDE this band, which is harmless (a
// visible pill next to visible pins), never the reverse.
export const isBelowMarkerHideFloor = (zoom) => (
  typeof zoom === 'number' && Number.isFinite(zoom) && zoom < MARKER_HIDE_FLOOR_ZOOM
);
